import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Calendar, User, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPosts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/blog?published_only=true`);
      setPosts(response.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen">
      <section
        data-testid="blog-hero-section"
        className="relative py-32 bg-gradient-to-br from-[#0A3D2E] to-[#07291F] text-white"
      >
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl sm:text-6xl font-bold tracking-tighter mb-6 font-['Outfit']">
              Blog & Insights
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              Latest news, guides, and insights on EV charging in South Africa.
            </p>
          </motion.div>
        </div>
      </section>

      <section data-testid="blog-posts-section" className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-[#4A5D54]">Loading posts...</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-[#4A5D54] mb-4">No blog posts yet. Check back soon!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post, index) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  data-testid={`blog-post-card-${index}`}
                >
                  <Link
                    to={`/blog/${post.slug}`}
                    className="block bg-white rounded-2xl border border-[#E2E8F0] overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all group h-full"
                  >
                    {post.image_url && (
                      <div className="aspect-video bg-[#F4F7F5] overflow-hidden">
                        <img
                          src={post.image_url}
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}
                    <div className="p-6">
                      <div className="flex items-center space-x-4 text-sm text-[#4A5D54] mb-3">
                        <span className="flex items-center">
                          <Calendar size={16} className="mr-1" />
                          {formatDate(post.created_at)}
                        </span>
                        <span className="flex items-center">
                          <User size={16} className="mr-1" />
                          {post.author}
                        </span>
                      </div>
                      <h3 className="text-xl font-semibold text-[#0A3D2E] mb-2 font-['Outfit'] group-hover:text-[#CDFF00] transition-colors">
                        {post.title}
                      </h3>
                      <p className="text-base text-[#4A5D54] mb-4 line-clamp-3">{post.excerpt}</p>
                      <span className="text-[#0A3D2E] font-semibold inline-flex items-center group-hover:text-[#CDFF00]">
                        Read More <ArrowRight size={18} className="ml-1" />
                      </span>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
